import { useEffect, useState } from 'react';
import { X, Mail, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useCustomer } from '@/hooks/useCustomer';
import { createStorefrontCustomer } from '@/lib/storefrontCustomer';

interface EmailCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (email: string, name: string) => void;
  /**
   * Soft-gate variant: if true, render a "continue without email" link that
   * fades in after 5 seconds. Used on Pro-tier feature gates (Sight Tape,
   * Shot Solver) so impatient power users can bypass. Default = false (always
   * skippable via X, but no explicit bypass link).
   */
  softGate?: boolean;
  /**
   * Context-specific lead-in copy. Used so the same modal can serve the
   * post-Save "build saved!" moment AND the passive 90s-of-use moment AND
   * the Pro-tier feature gate, each with the right framing.
   */
  context?: 'post-save' | 'passive' | 'pro-gate' | 'manual';
}

const STORAGE_KEY = 'sparrow_lead_email';

/**
 * Email capture modal — the lead-magnet engine for the open-preview phase.
 *
 * Wired to Shopify Storefront API customerCreate (see lib/storefrontCustomer.ts).
 * Customers land in Customers → Email subscribers with acceptsMarketing=true,
 * then a Shopify Flow rule auto-tags them `forge-tester` during the preview
 * window (June 2026). At launch (~June 14) we email all forge-tester customers
 * an early-adopter offer: $6/mo Hunter or $12/mo Pro, locked for 12 months.
 *
 * Three trigger points wire into the same modal with different `context`:
 *   - post-save  → fires AFTER Save Build succeeds (value delivered first)
 *   - passive    → fires after 90s of active Forge use, one-time
 *   - pro-gate   → fires BEFORE Sight Tape Print / Shot Solver (soft-gate, 5s skip)
 *
 * Persistence: cached in localStorage so we never re-show after capture.
 */
export function EmailCaptureModal({
  isOpen,
  onClose,
  onSubmit,
  softGate = false,
  context = 'manual',
}: EmailCaptureModalProps) {
  const customer = useCustomer();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [skipReady, setSkipReady] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Soft-gate timer: enable the "continue without email" link after 5s.
  // Resets every time the modal opens.
  useEffect(() => {
    if (!isOpen || !softGate) {
      setSkipReady(false);
      return;
    }
    const t = window.setTimeout(() => setSkipReady(true), 5000);
    return () => window.clearTimeout(t);
  }, [isOpen, softGate]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.includes('@')) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      // Cache locally first so even if the network call fails, we don't
      // re-prompt this user on the same device.
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ email, name, ts: Date.now() }));

      const result = await createStorefrontCustomer(email, name);

      if (!result.ok) {
        // Hard failure — surface a gentle error but still close on user retry
        setErrorMsg("Couldn't reach the server. Your email is saved locally — try again in a sec.");
        setSubmitting(false);
        return;
      }

      // Success OR already_subscribed both mean "we have them" from a UX
      // standpoint. No need to distinguish to the user.
      onSubmit(email, name);
      onClose();
    } catch {
      setErrorMsg("Couldn't reach the server. Your email is saved locally — try again in a sec.");
      setSubmitting(false);
    }
  };

  // Context-specific copy. The "promise" stays the same (lock in founding
  // pricing for 12 months) but the lead-in adapts to where the user is.
  const titleCopy =
    context === 'post-save'
      ? 'Build saved. Want it emailed?'
      : context === 'pro-gate'
        ? 'Pro feature — free during preview'
        : context === 'passive'
          ? 'Quick ask while you build'
          : 'Lock in founding pricing';

  const bodyCopy =
    context === 'post-save'
      ? "Drop your email and we'll send your build sheet — and lock you in at $6/mo Hunter or $12/mo Pro, guaranteed for 12 months when subscriptions go live (~June 14)."
      : context === 'pro-gate'
        ? "This is a Pro-tier feature — free for everyone during open preview. Drop your email to lock in Pro at $12/mo for 12 months when subscriptions launch (~June 14)."
        : context === 'passive'
          ? "You've been building for a bit — looks like The Forge is working for you. Drop your email to lock in founding pricing: $6 Hunter or $12 Pro, guaranteed for 12 months at launch (~June 14)."
          : "Drop your email to lock in $6/mo Hunter or $12/mo Pro, guaranteed for 12 months from launch (~June 14).";

  const submitCopy = submitting ? 'Locking in…' : 'Lock in founding pricing';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full p-6 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-100 transition"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="flex items-center justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-ember-500/10 border border-ember-500/30 flex items-center justify-center">
            <Mail size={20} className="text-ember-500" />
          </div>
        </div>

        <h2 className="text-xl font-bold text-center mb-1">{titleCopy}</h2>
        <p className="text-sm text-zinc-400 text-center mb-5 leading-relaxed">
          {bodyCopy}
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-zinc-500">Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Sal"
              className="
                w-full mt-1 bg-zinc-950 border border-zinc-800 rounded-md
                px-3 py-2 text-sm focus:outline-none focus:border-ember-500
              "
            />
          </div>
          <div>
            <label className="text-xs text-zinc-500">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoFocus
              className="
                w-full mt-1 bg-zinc-950 border border-zinc-800 rounded-md
                px-3 py-2 text-sm focus:outline-none focus:border-ember-500
              "
            />
          </div>

          {errorMsg && (
            <p className="text-[11px] text-red-400 leading-snug">{errorMsg}</p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="md"
            disabled={submitting || !email.includes('@')}
            leftIcon={<Sparkles size={14} />}
            className="w-full"
          >
            {submitCopy}
          </Button>

          <p className="text-[10px] text-zinc-500 text-center leading-snug">
            Sparrow is in open preview through ~June 14. Locked-in pricing means
            the launch rate is yours for 12 months from launch. Unsubscribe anytime —
            we never sell your email.
          </p>
        </form>

        {softGate && skipReady && (
          <button
            type="button"
            onClick={onClose}
            className="
              mt-3 w-full text-center text-[11px] text-zinc-500 hover:text-zinc-300
              underline-offset-2 hover:underline transition
            "
          >
            Continue without email →
          </button>
        )}

        <div className="mt-4 pt-4 border-t border-zinc-800/60">
          {customer ? (
            <p className="text-[10px] text-emerald-400/80 leading-snug text-center">
              ✓ Signed in — your builds sync across devices.
            </p>
          ) : (
            <p className="text-[10px] text-amber-400/80 leading-snug text-center">
              ⚠ Builds save on this device only. Sign in (top-right of the storefront)
              to sync across phones + laptops.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Check if we've already captured this user's email — if so, skip the modal
 * on subsequent saves. Returns the cached email + name, or null.
 */
export function getCachedLead(): { email: string; name: string } | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

